import { Box, Button, Divider, FormControl, FormHelperText, InputAdornment, InputLabel, OutlinedInput, TextField, Typography } from "@mui/material";
import { ISabor } from "../../../../interfaces/ISabor";
import { ButtonsContainer, FormularioCard } from "../../styles";
import { useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { saborSchema } from "../../../../schema/saborSchema";
import { FieldValues, useForm } from "react-hook-form";
import { v4 } from "uuid";
import { useMoeda } from "../../../../../core/hooks/useMoeda";
import { IInsumo } from "../../../../interfaces/IInsumo";
import { useCadastrarEditarProdutoContext } from "../../../../contexts/CadastrarEditarProdutoContext";
import { FichaTecnica } from "../../../FichaTecnica";
import { TipoControleEstoque } from "../../../../enum/TipoControleEstoque";
import { IImagemProduto } from "../../../../interfaces/IImagemProduto";
import { ImagemContainer } from "../../../ConfiguracaoPizzaContainer/styles";
import { CardUploadImagem } from "../../../../../core/components/CardUploadImagem";
import { useSnackbar } from "../../../../../core/contexts/SnackbarContext";
import { MIMETypes } from "../../../../../core/enum/MIMETypes";
import { IProcessoPreparoKDS } from "../../../../../kds/interfaces/IKDS";
import { ProcessoPreparoForm } from "../../../../../cozinha/components/ProcessoPreparoForm";
import { useEstacoesKDS } from "../../../../../kds/hooks/useEstacoesKDS";

interface FormularioSaborProps {
    adicionarSabor: (sabor: ISabor) => void;
    editarSabor: (sabor: ISabor) => void;
    saborEdicao?: ISabor;
    handleTab: (tab: "tabela" | "formulario") => void;
}

export const FormularioSabor = ({ adicionarSabor, editarSabor, handleTab, saborEdicao }: FormularioSaborProps) => {
    const { watch, setValue, reset, register, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(saborSchema),
        mode: "onChange",
    });

    const { controleEstoque } = useCadastrarEditarProdutoContext();
    const { estacoes } = useEstacoesKDS();

    const [fichaTecnica, setFichaTecnica] = useState<IInsumo[]>([]);
    const [imagem, setImagem] = useState<undefined | File | IImagemProduto>(undefined);
    const [processosPreparoKDS, setProcessosPreparoKDS] = useState<IProcessoPreparoKDS[]>([]);

    const adicionarInsumo = (insumo: IInsumo) => {
        setFichaTecnica(ficha => [...ficha, insumo]);
    }

    const excluirInsumo = (uuid: string) => {
        setFichaTecnica(ficha => ficha.filter(insumo => insumo.uuid !== uuid));
    }

    const { number } = useMoeda();

    const handleCadastrarSabor = async (sabor: FieldValues) => {
        const imagens: IImagemProduto[] = [];

        if (imagem) {
            imagens.push(imagem as IImagemProduto);
        }

        adicionarSabor({
            descricao: sabor.descricao,
            preco: number(sabor.preco),
            precoSemDesconto: number(sabor.precoSemDesconto),
            detalhes: sabor.detalhes,
            uuid: v4(),
            fichaTecnica: fichaTecnica ?? [],
            imagens,
            processosPreparoKDS
        });
        handleTab("tabela")
    }

    const mimetypesValidos = [
        MIMETypes["image/jpeg"],
        MIMETypes["image/jpg"],
        MIMETypes["image/png"]
    ] as string[];

    const snackbar = useSnackbar();

    const handleUploadImagem = (arquivo: File) => {
        if (!mimetypesValidos.includes(arquivo.type)) {
            return snackbar(`Arquivo inválido, tipos aceitos: (PNG, JPEG)`, { severity: "error" })
        }

        setImagem(arquivo);
    }

    const handleExcluirImagem = () => {
        setImagem(undefined);
    }

    const handleEditarSabor = async (sabor: FieldValues) => {
        if (saborEdicao) {
            const imagens: IImagemProduto[] = [];

            if (imagem) {
                imagens.push(imagem as IImagemProduto);
            }

            editarSabor({
                descricao: sabor.descricao,
                detalhes: sabor.detalhes,
                preco: number(sabor.preco),
                precoSemDesconto: number(sabor.precoSemDesconto),
                uuid: saborEdicao.uuid,
                fichaTecnica: fichaTecnica ?? [],
                imagens,
                processosPreparoKDS
            })
            handleTab("tabela")
        }
    }

    useEffect(() => {
        if (saborEdicao) {
            setValue("descricao", saborEdicao.descricao);
            setValue("detalhes", saborEdicao.detalhes);
            setValue("preco", saborEdicao.preco);
            setValue("precoSemDesconto", saborEdicao.precoSemDesconto);
            setFichaTecnica(saborEdicao.fichaTecnica ?? []);
            setProcessosPreparoKDS(saborEdicao.processosPreparoKDS ?? []);
            if (saborEdicao.imagens?.length) {
                setImagem(saborEdicao.imagens[0]);
            }
        } else {
            reset();
            setFichaTecnica([]);
            setImagem(undefined);
            setProcessosPreparoKDS([]);
        }
    }, [saborEdicao])

    const descricao = watch("descricao");
    const preco = watch("preco");
    const precoSemDesconto = watch("precoSemDesconto");
    const detalhes = watch("detalhes");

    return (
        <FormularioCard>
            <Typography variant="h6">
                Cadastrar sabor
            </Typography>
            <form onSubmit={handleSubmit(saborEdicao ? handleEditarSabor : handleCadastrarSabor)}>
                <TextField
                    autoFocus
                    margin="dense"
                    id="name"
                    label="Nome"
                    type="text"
                    fullWidth
                    {...register("descricao")}
                    error={!!errors.descricao}
                    helperText={<>{errors.descricao?.message}</>}
                    value={descricao}
                />
                <TextField
                    type="text"
                    placeholder="Detalhes"
                    label="Detalhes"
                    {...register("detalhes")}
                    value={detalhes}
                    fullWidth
                    error={!!errors.detalhes}
                    helperText={<>{errors.detalhes?.message}</>}
                    multiline
                    sx={{ mt: 3 }}
                    minRows={3}
                    maxRows={12}
                />
                <FormControl fullWidth sx={{ mt: 3 }}>
                    <InputLabel error={!!errors.preco} htmlFor="preco-sabor">Preço</InputLabel>
                    <OutlinedInput
                        id="preco-sabor"
                        error={!!errors.preco}
                        startAdornment={<InputAdornment position="start">R$</InputAdornment>}
                        label="Preço"
                        value={preco}
                        {...register("preco")}
                    />
                    {!!errors.preco && <FormHelperText error={true}><>{errors.preco?.message}</></FormHelperText>}
                </FormControl>
                <FormControl fullWidth sx={{ mt: 3 }}>
                    <InputLabel error={!!errors.precoSemDesconto} htmlFor="preco-sabor-sem-desconto">Preço sem desconto</InputLabel>
                    <OutlinedInput
                        id="preco-sabor-sem-desconto"
                        error={!!errors.precoSemDesconto}
                        startAdornment={<InputAdornment position="start">R$</InputAdornment>}
                        label="Preço sem desconto"
                        value={precoSemDesconto}
                        {...register("precoSemDesconto")}
                    />
                    {!!errors.precoSemDesconto && <FormHelperText error={true}><>{errors.precoSemDesconto?.message}</></FormHelperText>}
                </FormControl>
                <ImagemContainer>
                    <CardUploadImagem
                        titulo="Imagem"
                        descricao={
                            imagem
                                ? (imagem instanceof File ? imagem?.name : (imagem?.nomeOriginal ?? imagem?.nome))
                                : "Faça o upload"
                        }
                        url={imagem instanceof File ? URL.createObjectURL(imagem) : imagem?.url}
                        excluir={handleExcluirImagem}
                        upload={handleUploadImagem}
                    />
                </ImagemContainer>
                {controleEstoque == TipoControleEstoque.completo && (
                    <FichaTecnica fichaTecnica={fichaTecnica} adicionarInsumo={adicionarInsumo} excluirInsumo={excluirInsumo} />
                )}
                <Box sx={{ mt: 3 }}>
                    <Divider sx={{ mb: 2 }} />
                    <ProcessoPreparoForm
                        processos={processosPreparoKDS}
                        estacoes={estacoes}
                        onAdicionar={async (dados) => setProcessosPreparoKDS(prev => [...prev, { ...dados, uuid: v4() }])}
                        onAtualizar={async (uuid, dados) => setProcessosPreparoKDS(prev => prev.map(p => p.uuid === uuid ? { ...p, ...dados } : p))}
                        onRemover={async (uuid) => setProcessosPreparoKDS(prev => prev.filter(p => p.uuid !== uuid).map(p => ({ ...p, dependeDe: p.dependeDe.filter(d => d !== uuid) })))}
                    />
                </Box>
                <ButtonsContainer>
                    <Button variant="outlined" type="button" onClick={() => handleTab("tabela")}>Cancelar</Button>
                    <Button variant="contained" type="submit">{saborEdicao ? "Atualizar" : "Cadastrar"}</Button>
                </ButtonsContainer>
            </form>
        </FormularioCard>
    );
}